import { GraphQLError, GraphQLErrorOptions } from "graphql";

export const FORBIDDEN_ERROR_CODE = "FORBIDDEN";
export const UNAUTHENTICATED_ERROR_CODE = "UNAUTHENTICATED";
export const USER_INPUT_ERROR_CODE = "BAD_REQUEST";
export const NOT_FOUND_ERROR_CODE = "NOT_FOUND";
export const CONFLICT_ERROR_CODE = "CONFLICT";
export const EXTERNAL_SERVICE_ERROR_CODE = "EXTERNAL_SERVICE_ERROR";
export const RATE_LIMITED_ERROR_CODE = "RATE_LIMITED";
export const ALB_RESPONSE_LIMIT_EXCEEDED_MESSAGE = "Response body exceeds ALB limit";

type RSErrorOptions = GraphQLErrorOptions & {
  // Allows us to hide errors we don't need to report to sentry
  //  i.e expected outcome of normal app usage (bad input, missing
  //  permission, stale reference, etc)
  reportToSentry?: boolean;
};

export class RSError extends GraphQLError {
  reportToSentry: boolean;

  constructor(message: string, code: string, { reportToSentry = true, ...options }: RSErrorOptions = {}) {
    super(message, { ...options, extensions: { ...(options.extensions ?? {}), code } });
    this.reportToSentry = reportToSentry;
  }
}

export class ForbiddenError extends RSError {
  constructor(message?: string, { reportToSentry = false, ...options }: RSErrorOptions = {}) {
    super(message ?? "Forbidden", FORBIDDEN_ERROR_CODE, { ...options, reportToSentry });
  }
}

export class AuthenticationError extends RSError {
  constructor(message?: string, options?: GraphQLErrorOptions) {
    super(message ?? "Unauthenticated", UNAUTHENTICATED_ERROR_CODE, {
      ...options,
      extensions: {
        ...options?.extensions,
        http: {
          status: 401,
        },
      },
    });
  }
}

export class UserInputError extends RSError {
  constructor(message?: string, { reportToSentry = false, ...options }: RSErrorOptions = {}) {
    super(message ?? "Bad Request", USER_INPUT_ERROR_CODE, { ...options, reportToSentry });
  }
}

export class NotFoundError extends RSError {
  constructor(message?: string, { reportToSentry = false, ...options }: RSErrorOptions = {}) {
    super(message ?? "Not Found", NOT_FOUND_ERROR_CODE, { ...options, reportToSentry });
  }
}

export class ConflictError extends RSError {
  constructor(message?: string, { reportToSentry = false, ...options }: RSErrorOptions = {}) {
    super(message ?? "Conflict", CONFLICT_ERROR_CODE, { ...options, reportToSentry });
  }
}

export class ExternalServiceError extends RSError {
  constructor(message?: string, options?: RSErrorOptions) {
    super(message ?? "Bad Gateway", EXTERNAL_SERVICE_ERROR_CODE, options);
  }
}

export class RateLimitError extends RSError {
  constructor(message?: string, options?: RSErrorOptions) {
    super(message ?? "Too Many Requests", RATE_LIMITED_ERROR_CODE, options);
  }
}
